import React, { useState, useEffect } from "react";
import { Input, Tag, Modal, Checkbox, Button, Typography } from "antd";
import DataTable from '@/components/DataTable';
import { CheckCircleOutlined, CloseCircleOutlined, CloseOutlined } from "@ant-design/icons";
import MainCard from '@/components/MainCard';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import {
  get_standard_checklist,
  get_ticket_Stage_checklist,
  Submit_Standard_Checklist,
} from '@/utils/api';
import { defaultColors } from '@/themes/antd-theme';
import Icons from '@/components/third-party/Icons';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/App';
import { formatDateTime } from '@/utils/datetime';

const { Title } = Typography;

const { TextArea } = Input;

const StandardChecklist = ({
  onClose,
  updateBatchStatus,
  ticket_id,
  batchId,
  set_refresh,
  refresh
}) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const [checklist, setChecklist] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        get_ticket_Stage_checklist({ ticket_id: ticket_id }, (response) => {
          console.log("Full API Response:", response.data);

          if (response.data && Array.isArray(response.data.data)) {
            setChecklist(response.data.data);
          } else {
            setChecklist([]);
          }
        });
      } catch (e) {
        setError(e);
        console.error("Fetching data failed", e);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [ticket_id, refresh]);

  const [issueDescription, setIssueDescription] = useState("");
  const [validationErrors, setValidationErrors] = useState([]);
  const [isValidating, setIsValidating] = useState(false);

  useEffect(() => {
    if (isValidating) {
      const interval = setInterval(() => {
        const errors = checklist
          .filter((item) => !item.completed)
          .map((item) => item.task);
        setValidationErrors(errors);
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [checklist, isValidating]);

  const handleToggle = (id) => {
    setChecklist(
      checklist.map((item) =>
        item.id === id ? { ...item, completed: !item.completed } : item
      )
    );
  };

  const handleSubmit = async () => {
    const errors = checklist
      .filter((item) => !item.completed)
      .map((item) => item.param);

    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    const checklistIds = checklist
      .filter((item) => item.completed)
      .map((item) => ({ id: item.id }));

    const configReq = {
      ticket_id: ticket_id,
      remark: issueDescription,
      checklist_ids: checklistIds,
    };

    try {
      onClose();
      await Submit_Standard_Checklist(configReq, (response) => {
        console.log(response);

        if (response?.status === "success") {
          toast.success(
            'Review submitted successfully! Status changed to "Completed"'
          );
          updateBatchStatus(batchId, checklistIds);
          const completedChecklist = checklist.filter((item) => item.completed);
          setChecklist(completedChecklist);
          set_refresh(!refresh);
          setIssueDescription("");
          setValidationErrors([]);
          setIsValidating(false);
          onClose();
        } else {
          toast.warning(response?.data?.message);
        }
      });
    } catch (error) {
      console.error("Error submitting checklist:", error);
      toast.error("Failed to submit the checklist. Please try again.");
    }
  };

  return (
    <div className="max-w-3xl mx-auto mt-2 p-6 bg-white dark:bg-gray-800 rounded-lg shadow">
      <div className="mb-4">
        <div className="space-y-2 mb-4">
          {checklist.map((item) => (
            <div
              key={item.id}
              className="flex items-center p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded cursor-pointer"
              onClick={() => handleToggle(item.id)}
            >
              <Checkbox
                checked={item.completed}
                style={{
                  color: defaultColors.primary,
                }}
              />
              <span className="ml-3 text-base">{item.param}</span>
            </div>
          ))}
        </div>

        {validationErrors.length > 0 && (
          <div
            className="mb-4 p-4 rounded"
            style={{
              backgroundColor: defaultColors.palette.red[50],
            }}
          >
            <p className="text-sm mb-2" style={{ color: defaultColors.error }}>
              Please complete the following steps:
            </p>
            <ul className="list-disc list-inside">
              {validationErrors.map((error, index) => (
                <li key={index} className="text-sm" style={{ color: defaultColors.error }}>
                  {error}
                </li>
              ))}
            </ul>
          </div>
        )}

        <TextArea
          rows={3}
          placeholder="Issue Description (Optional)"
          value={issueDescription}
          onChange={(e) => setIssueDescription(e.target.value)}
          className="mb-4"
        />

        <div className="flex justify-end">
          <Button
            type="primary"
            size="large"
            onClick={handleSubmit}
            style={{
              backgroundColor: defaultColors.primary,
              borderColor: defaultColors.primary,
              fontWeight: 'bold',
            }}
          >
            Complete Review
          </Button>
        </div>
      </div>
    </div>
  );
};

const BatchProcessingPage = () => {
  const [open, setOpen] = useState(false);
  const [batchData, setBatchData] = useState([]);
  const [ticketdetails, setTicketDetails] = useState();
  const [refresh, set_refresh] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        get_standard_checklist((response) => {
          if (
            response.data &&
            response.data.data &&
            Array.isArray(response.data.data)
          ) {
            const formattedData = response.data.data.map((item) => ({
              batchId: item.batch_id ?? `Ticket-${item.ticket_id}`,
              issueType: item.issue_type,
              action: item.action,
              createdDate: formatDateTime(item.created_at),
              department: item.department,
              status: "Pending",
              ticket_id: item.ticket_id,
            }));

            setBatchData(formattedData);
          } else {
            console.error(
              "Expected response.data.data to be an array, but got:",
              response.data
            );
          }
        });
      } catch (e) {
        console.error("Fetching data failed", e);
      }
    };

    fetchData();
  }, [refresh]);

  const handleOpen = (batchId, ticket_id) => {
    setOpen(batchId);
    setTicketDetails(ticket_id);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const updateBatchStatus = (batchId, completedChecklistIds) => {
    setBatchData((prevBatchData) =>
      prevBatchData?.map((batch) => {
        if (batch.batchId === batchId) {
          const isCompleted = completedChecklistIds.some(
            (item) => item.id === batch.ticket_id
          );
          return { ...batch, status: isCompleted ? "Completed" : batch.status };
        }
        return batch;
      })
    );
  };

  const refreshBatchData = async () => {
    try {
      const response = await get_standard_checklist();
      if (
        response.data &&
        response.data.data &&
        Array.isArray(response.data.data)
      ) {
        const formattedData = response.data.data.map((item) => ({
          batchId: item.batch_id ?? `Ticket-${item.ticket_id}`,
          issueType: item.issue_type,
          action: item.action,
          createdDate: formatDateTime(item.created_at),
          department: item.department,
          status: "Pending",
          ticket_id: item.ticket_id,
        }));
        setBatchData(formattedData);
      } else {
        console.error(
          "Expected response.data.data to be an array, but got:",
          response.data
        );
      }
    } catch (e) {
      console.error("Fetching data failed", e);
    }
  };

  const columns = [
    {
      title: "Issue Type",
      dataIndex: "issueType",
      key: "issueType",
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (action) => <Tag color="blue">{action}</Tag>,
    },
    {
      title: "Created Date",
      dataIndex: "createdDate",
      key: "createdDate",
    },
    {
      title: "Department",
      dataIndex: "department",
      key: "department",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "Pending" ? "orange" : "green"}>{status}</Tag>
      ),
    },
    {
      title: "Review",
      key: "action",
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          style={{
            backgroundColor: defaultColors.primary,
            borderColor: defaultColors.primary,
          }}
          onClick={() => handleOpen(record.batchId, record.ticket_id)}
          disabled={record.batchId.startsWith("Ticket-")}
        >
          Review
        </Button>
      ),
    },
  ];

  return (
    <MainCard
      title={
        <div className="flex items-center gap-2">
          <Icons.CheckSquare className="text-blue-600" size={24} />
          <Title level={4} style={{ color: textColor, margin: 0, padding: 0 }}>
            {t('Standard Checklist')}
          </Title>
        </div>
      }
    >
      <DataTable columns={columns} data={batchData} rowKey="batchId" size="middle" height={null} pagination={{ pageSize: 10 }} />

      <Modal
        open={Boolean(open)}
        onCancel={handleClose}
        footer={null}
        width={500}
        title={
          <div className="flex justify-between items-center">
            <span className="text-lg font-bold">
              Standard Checklist - Batch ID: {open}
            </span>
          </div>
        }
        closeIcon={<CloseOutlined />}
      >
        <StandardChecklist
          onClose={handleClose}
          updateBatchStatus={updateBatchStatus}
          batchId={open}
          refresh={refresh}
          set_refresh={set_refresh}
          ticket_id={ticketdetails}
        />
      </Modal>

      <ToastContainer />
    </MainCard>
  );
};

export default BatchProcessingPage;
